// @flow
import { startRequest, finishRequest } from "@store/utils";
import { LOCATION_CHANGE } from "connected-react-router";
import {
  ExportAbsenceDetailsReport,
  ExportAbsenceDetailsReportSuccess,
  GetAbsenceRecordSearchFiltersSchema,
  GetAbsenceRecordSearchFiltersSchemaSuccess,
  GetAbsenceRecordSearchFiltersSchemaError,
  GetAbsenceDefaultReports,
  GetAbsenceDefaultReportsSuccess,
  GetAbsenceDefaultReportsError,
  GetAbsenceMySavedReports,
  GetAbsenceMySavedReportsSuccess,
  GetAbsenceMySavedReportsError,
  GetAbsenceDetailsReport,
  GetAbsenceDetailsReportSuccess,
  GetAbsenceDetailsReportError,
  DeleteSavedAbsenceDetailsReport,
  DeleteSavedAbsenceDetailsReportError,
  DeleteSavedAbsenceDetailsReportSuccess,
  UpdateSavedAbsenceDetailsReport,
  UpdateSavedAbsenceDetailsReportSuccess,
  UpdateSavedAbsenceDetailsReportError,
  GetAbsenceDetailsChartsSuccess,
  GetAbsenceDetailsCharts,
  GetAbsenceDetailsChartsError,
  SetAbsenceDetailsReportToSave,
  SetAbsenceDetailsReportViewState,
  ExportAbsenceDetailsReportError,
} from "@modules/reporting/constants/actions";
import type { AbsenceDetailsState, AbsenceDetailsReportViewState } from "./types";
import { cloneObject } from "@utils/object";

export const initialViewState: AbsenceDetailsReportViewState = {
  filtersExpanded: false,
  chartsExpanded: true,
  reportTableExpanded: true,
};

export const initialState: AbsenceDetailsState = {
  error: null,
  requestsInProgress: 0,
  reportGenerateInProgress: 0,
  absenceRecordSearchFiltersSchema: null,
  defaultReports: [],
  mySavedReports: [],
  currentReport: null,
  currentCharts: null,
  reportData: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  reportToSave: null,
  viewState: initialViewState,
};

export const setLoading = (state: AbsenceDetailsState) => () => startRequest(state);

export const setLoaded = (state: AbsenceDetailsState) => () => finishRequest(state);

export const onGetAbsenceRecordSearchFiltersSchemaSuccess = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    absenceRecordSearchFiltersSchema: payload,
  };
};

export const onGetAbsenceDefaultReportsSuccess = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    defaultReports: payload,
  };
};

export const onGetAbsenceDetailsReportSuccess = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    reportData: payload,
  };
};

export const onGetAbsenceDetailsReport = (state: AbsenceDetailsState) => ({ payload }: Object) => {
  return {
    ...startRequest(state),
    currentReport: payload,
  };
};

export const onGetAbsenceDetailsChartsSuccess = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    currentCharts: payload,
  };
};

export const onGetAbsenceMySavedReportsSuccess = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...finishRequest(state),
    error: null,
    mySavedReports: payload,
  };
};

export const onError = (state: AbsenceDetailsState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onSetAbsenceDetailsReportToSave = (state: AbsenceDetailsState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    reportToSave: payload,
  };
};

export const onSetViewState = (state: AbsenceDetailsState) => ({ payload }: Object) => {
  return {
    ...state,
    viewState: payload,
  };
};

export const onResetViewState = (state: AbsenceDetailsState) => () => ({
  ...state,
  viewState: initialViewState,
});

export const onExportAbsenceDetailsReport = (state: AbsenceDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress: ++state.reportGenerateInProgress,
  };
};

export const onExportAbsenceDetailsReportLoaded = (state: AbsenceDetailsState) => () => {
  return {
    ...state,
    reportGenerateInProgress:
      state.reportGenerateInProgress > 0 ? --state.reportGenerateInProgress : 0,
  };
};

export default (state: AbsenceDetailsState = initialState, action: Object): AbsenceDetailsState => {
  const act = {
    [ExportAbsenceDetailsReport]: onExportAbsenceDetailsReport,
    [ExportAbsenceDetailsReportSuccess]: onExportAbsenceDetailsReportLoaded,
    [ExportAbsenceDetailsReportError]: onExportAbsenceDetailsReportLoaded,
    [GetAbsenceDefaultReports]: setLoading,
    [GetAbsenceDefaultReportsSuccess]: onGetAbsenceDefaultReportsSuccess,
    [UpdateSavedAbsenceDetailsReport]: setLoading,
    [UpdateSavedAbsenceDetailsReportSuccess]: setLoaded,
    [GetAbsenceDetailsReport]: onGetAbsenceDetailsReport,
    [GetAbsenceDetailsReportSuccess]: onGetAbsenceDetailsReportSuccess,
    [GetAbsenceMySavedReports]: setLoading,
    [GetAbsenceMySavedReportsSuccess]: onGetAbsenceMySavedReportsSuccess,
    [GetAbsenceRecordSearchFiltersSchema]: setLoading,
    [DeleteSavedAbsenceDetailsReport]: setLoading,
    [DeleteSavedAbsenceDetailsReportSuccess]: setLoaded,
    [GetAbsenceDetailsChartsSuccess]: onGetAbsenceDetailsChartsSuccess,
    [GetAbsenceDetailsCharts]: setLoading,
    [GetAbsenceRecordSearchFiltersSchemaSuccess]: onGetAbsenceRecordSearchFiltersSchemaSuccess,
    [SetAbsenceDetailsReportToSave]: onSetAbsenceDetailsReportToSave,
    [SetAbsenceDetailsReportViewState]: onSetViewState,
    [GetAbsenceDefaultReportsError]: onError,
    [UpdateSavedAbsenceDetailsReportError]: onError,
    [GetAbsenceDetailsReportError]: onError,
    [GetAbsenceMySavedReportsError]: onError,
    [DeleteSavedAbsenceDetailsReportError]: onError,
    [GetAbsenceDetailsChartsError]: onError,
    [GetAbsenceRecordSearchFiltersSchemaError]: onError,
    [LOCATION_CHANGE]: onResetViewState,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
